// `shortlist-candidate-profile-not-found.tpl.html` — what the frame shows when a profile the
// URL names cannot be opened.
//
// ⚠️ IT SPENDS ITS FIRST TWO SECONDS PRETENDING TO LOAD. `$onInit` sets `isLoading = true` and a
// `$timeout(…, 2000)` clears it — there is no request behind that, and nothing is being waited
// for. The spinner exists so the message does not flash up before a user has finished reading
// the click that got them here. Rendering the message immediately is a nicer screen and a
// different one, and the reference is the settled state, so a port that skips the delay matches
// the pixels and loses the behaviour.
import { createElement, useEffect, useState, type ReactElement } from 'react'
import { asset } from '../assetBase.ts'

/** `ExtensionProduct === ClientContacts ? 'Contact' : 'Candidate'`. */
export type NotFoundEntity = 'candidates' | 'clientContacts'

export interface ProfileNotFoundProps {
  entity?: NotFoundEntity
  /** `timeToLoaded` — exposed so a test does not wait two seconds. */
  loadingMs?: number
  /**
   * `ExtensionViewWidth.Extended` (378) — the state's `maxWidth`, as a `flex-basis` on the
   * ELEMENT.
   *
   * ⚠️ IT IS NOT SLIM. The review and edit screens use `Slim` and `Full`; this one is the only
   * `Extended`, and it is what CENTRES the content — `align-items: center` on a box that fills
   * the frame centres against the frame, which at 1146px puts the image nowhere near where
   * production has it. Width supplied by the route again, invisible in the stylesheet.
   */
  flexBasis?: number
}

export function ProfileNotFound({
  entity = 'candidates',
  loadingMs = 2000,
  flexBasis,
}: ProfileNotFoundProps): ReactElement {
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), loadingMs)
    return () => clearTimeout(timer)
  }, [loadingMs])

  // `entityName` — the word, not the product key.
  const entityName = entity === 'clientContacts' ? 'Contact' : 'Candidate'

  const content = (
    <div className="shortlist-candidate-profile-not-found-container">
      {loading ? (
        <div className="shortlist-candidate-profile-not-found-content">
          <i className="fa fa-spinner fa-spin shortlist-candidate-profile-not-found-loading" />
        </div>
      ) : (
        <div className="shortlist-candidate-profile-not-found-content">
          <div className="shortlist-candidate-profile-not-found-image">
            <img src={asset('/static/assets/img/extension/extension-candidate-not-found.png')} alt="" />
          </div>
          {/* A bare `<span>` — the stylesheet targets the element, not a class. */}
          <span>{entityName} not found</span>
        </div>
      )}
    </div>
  )

  // ⚠️ A WRAPPER DIV WHOSE CLASS MATCHES THE ELEMENT'S NAME, AND BOTH ARE REAL. The state
  // template writes `<div class="shortlist-candidate-profile-not-found">` around
  // `<shortlist-candidate-profile-not-found>`; the div is the flex parent
  // (`_extension.state.tabs.scss`) and the element carries the width. Rendering either alone
  // leaves the content hugging the left edge of whatever width the frame happens to be.
  return (
    <div className="shortlist-candidate-profile-not-found">
      {createElement(
        'shortlist-candidate-profile-not-found',
        flexBasis === undefined ? null : { style: { flexBasis: `${String(flexBasis)}px` } },
        content,
      )}
    </div>
  )
}
